/**
 * Spaces API - Single-space detail
 *
 * GET /v1/spaces/:spaceId
 *
 * The canonical single-space read. `SpaceDetailSchema` EXTENDS the list-row
 * contract (`SpaceListItemSchema`) with the detail-only fields the mobile
 * space-detail / settings screen stitches together today (description, feature
 * toggles + notify default, optional roster summary, assignments/schedule
 * capability flags). Extending — not composing — keeps a `SpaceListItem`
 * assignable into a `SpaceDetail`, so the list row can prime the detail query
 * without a shape conversion (plan KTD2 / R4).
 *
 * The Stream chat binding (`streamChatChannelId` / `streamChatStatus`) is
 * INHERITED from the list schema (where it is `.nullable().optional()` — present
 * only on community rows). It is deliberately NOT redeclared here: narrowing an
 * optional field to required would break the list→detail assignability the
 * priming path relies on.
 */

import { z } from 'zod'
import { GroupDesignThemeSchema, NotificationLevelSchema } from '../groups/common'
import { AppVisibilitySchema } from './common'
import { SpaceListItemSchema } from './list'

// ============================================================================
// GET /v1/spaces/:spaceId
// ============================================================================

/**
 * The space's feature toggles + the viewer's notify default. The boolean
 * toggles mirror the mobile `buildSpaceSettingsViewModel` feature Pick exactly
 * so the settings screen renders straight from this block. Backend-default
 * parity (the server applies these when a column is unset, matching
 * `groups.prisma`):
 *   - `chatEnabled`         default **false**
 *   - `mediaLibraryEnabled` default **true**
 *   - `memberMediaEnabled`  `.optional().default(false)` — additive-optional for
 *     skew safety (an old backend that omits it still parses), same posture as
 *     `muted` and the viewer signals.
 * The remaining toggles are always populated on the wire.
 */
export const SpaceSettingsSchema = z.object({
  chatEnabled: z.boolean(),
  memberPostingEnabled: z.boolean(),
  commentsEnabled: z.boolean(),
  reactionsEnabled: z.boolean(),
  sharingEnabled: z.boolean(),
  mediaLibraryEnabled: z.boolean(),
  memberListEnabled: z.boolean(),
  // Member media contribute toggle (plan 2026-07-22-002 U1 / R4 / KTD7): may
  // plain active members ADD media to EXISTING albums in this space. Governs
  // only the L3 member tier of `resolve-media-authority` — leaders, moderator
  // grantees, and admins are authorized regardless. Independent of
  // `memberPostingEnabled` by design (shared tier shape, separate switch).
  // `.optional().default(false)` — additive-optional like `muted` for skew
  // safety (an old backend that omits it still parses), with the fail-closed
  // default applied at the parse boundary.
  memberMediaEnabled: z.boolean().optional().default(false),
  // The space's notify level (ALL / MENTIONS / IMPORTANT / NONE), VIEWER-RESOLVED
  // for BOTH section and community kinds: it is the viewer's own per-membership
  // preference, distinct from the section/space ENTITY default
  // (`Section.defaultNotifyLevel` / `UserGroup.defaultNotifyLevel`, exposed via
  // `adminDetail.defaultNotifyLevel`). For COMMUNITY spaces this has always been
  // viewer-resolved (null when the viewer has no preference row); the per-viewer
  // SECTION sourcing (`SectionMembership.notifyLevel`) lands in a later backend
  // unit — this contract just needs to permit it. Stays nullable because a
  // viewer may have no preference row. Kept as a loose nullable string on the wire;
  // enum-tightening to NotificationLevel is deferred because it requires a paired
  // cast at the backend emit point (services/spaces/crud/get-single-space.ts).
  notifyLevel: z.string().nullable(),
  // Per-viewer mute state, VIEWER-RESOLVED like `notifyLevel` (sourced from
  // `UserFeedMute` for both kinds). Additive-optional so an older mobile build /
  // a not-yet-deployed backend that omits it still parses (skew safety).
  muted: z.boolean().optional()
})

export type SpaceSettings = z.infer<typeof SpaceSettingsSchema>

/**
 * Section-only auto-publish sub-block. Carries the entity-canonical auto-publish
 * configuration that exists on `Section` but has no community (`UserGroup`)
 * counterpart, so the whole sub-block is OPTIONAL and is ABSENT for community
 * spaces. Every field inside is optional too (admin columns may be unset).
 */
export const AdminAutoPublishSchema = z.object({
  autoPublishEnabled: z.boolean().optional(),
  autoPublishNews: z.boolean().optional(),
  autoPublishAnnouncements: z.boolean().optional(),
  autoPublishEvents: z.boolean().optional(),
  defaultNotificationMode: z.string().nullable().optional()
})

export type AdminAutoPublish = z.infer<typeof AdminAutoPublishSchema>

/**
 * Admin-only detail block, populated ONLY for admin viewers.
 *
 * IMPORTANT: this block is populated only when the viewer is an admin. For
 * non-admin viewers it is ABSENT (the `adminDetail` key is omitted entirely —
 * NOT `null`), so consumers must treat "no `adminDetail`" as "not an admin
 * read", never as "admin field is empty". Every field inside is optional so the
 * backend projects only the columns it has, and so the block never narrows an
 * inherited list-row field to required (which would break list→detail priming).
 *
 * Carries entity-canonical values for admin edits — never viewer projections.
 * In particular `defaultNotifyLevel` is the ENTITY notify default
 * (`Section.defaultNotifyLevel` / `UserGroup.defaultNotifyLevel`). For COMMUNITY
 * kind, `settings.notifyLevel` stays viewer-resolved (the viewer's own
 * membership preference), so admins must read the entity default from
 * `adminDetail.defaultNotifyLevel`, not from `settings.notifyLevel`.
 */
export const AdminSpaceDetailSchema = z.object({
  // Design / theme (entity-canonical). Mirrors the GroupTheme field shapes.
  bannerBackgroundColor: z.string().nullable().optional(),
  bannerLogoUrl: z.string().nullable().optional(),
  bannerImageUrl: z.string().nullable().optional(),
  avatarImageUrl: z.string().nullable().optional(),
  designTheme: GroupDesignThemeSchema.nullable().optional(),
  // Visibility / identity write inputs.
  showInApp: z.boolean().optional(),
  betaTestersOnly: z.boolean().optional(),
  emailSlug: z.string().nullable().optional(),
  contactEnabled: z.boolean().optional(),
  appVisibility: AppVisibilitySchema.optional(),
  // Stream feed group (entity-canonical; exists on both kinds).
  streamFeedGroup: z.string().nullable().optional(),
  // Entity notify default (NOT the viewer-resolved `settings.notifyLevel`).
  defaultNotifyLevel: NotificationLevelSchema.nullable().optional(),
  // Section-only auto-publish sub-block; ABSENT for community spaces.
  autoPublish: AdminAutoPublishSchema.optional()
})

export type AdminSpaceDetail = z.infer<typeof AdminSpaceDetailSchema>

/**
 * Server-computed viewer capability block. A DISPLAY HINT for client gating
 * (which Settings rows / leader tools to show), mirroring the `canManage`
 * list-row precedent — it is NOT a write gate. Writes still authorize
 * independently server-side via `requireSectionManager` / `requireGroupManager`,
 * so a client that ignores or spoofs this block cannot escalate a write.
 *
 *   - `canManageSpace` — the viewer can manage this space (leader/admin),
 *     correct for both section and community kinds (reuses the use-case's
 *     `isAdminLike` + canonical leadership).
 *   - `isMember` — the viewer is a member of this space; a DISTINCT signal from
 *     `canManageSpace` (an admin may manage a space without being a member).
 *
 * Grant-capability signals (plan 2026-07-14-003, U1 / KTD2 / R16 / R22). THREE
 * signals, not one boolean — each fronts a DIFFERENT write gate, and one boolean
 * cannot express the viewer axis:
 *
 *   - `canGrantModerator` — the viewer may make/dismiss a MODERATOR on this
 *     space. Community: super-admin OR school-admin OR group OWNER/ADMIN
 *     (mirrors `member-permissions-update`). Section: the
 *     `requireSectionManager` rule minus the legacy-mirror `via:'manager'`
 *     bypass (mirrors `section-member-grant`).
 *   - `canGrantAdmin` — the viewer may grant/remove the ADMIN *management* level.
 *     Community only; ALWAYS `false` for a section (`ADMIN` is a community
 *     concept). This is the case a single boolean got wrong — a plain group ADMIN
 *     has `canGrantModerator: true` but `canGrantAdmin: false`.
 *
 *     ⚠ **Deliberately STRICTER than the server; do NOT "fix" it to match.**
 *     This signal is OWNER-equivalent only (group OWNER, or an owner-equivalent
 *     super/school admin). That is exactly faithful for the REMOVE direction:
 *     `member-permissions-update` admits only an owner-equivalent to demote an
 *     ADMIN. It is deliberately stricter for the GRANT direction: that endpoint
 *     currently lets ANY group ADMIN promote MEMBER → ADMIN, i.e. admins can mint
 *     admins — which contradicts the non-transitivity principle this whole design
 *     rests on (a grantee must not become a grantor). The plan (KTD2 / R21) chose
 *     the stricter rule knowingly; the server is the lax side and its promote gap
 *     is filed as a separate defect. Aligning this signal to the server would
 *     widen real authority and must not happen without revisiting the plan.
 *
 *   - `canRemoveMembers` — the viewer may remove members from this space.
 *     Mirrors the gate of **`POST /v1/groups/:group_id/members/remove`**
 *     (`member-remove`) — the route this feature is pinned to. NOT
 *     `DELETE /v1/groups/:group_public_id/members/:user_id`, which is a different
 *     endpoint with different (missing) authorization; do not wire this signal to
 *     it, however convenient its mobile wrapper is.
 *
 *     Gate mirrored: an ACTIVE membership in the group with a moderator-or-higher
 *     level AND `canModerate`. It has **NO super-admin / school-admin bypass**,
 *     unlike `member-permissions-update` — so a non-member school admin reads
 *     `canGrantModerator: true` but `canRemoveMembers: false`. NOT derivable from
 *     either grant signal, and NOT `kind === 'community'`.
 *
 *     ⚠ **VIEWER-scoped only — this is NOT "this row is removable."** It answers
 *     *"may this viewer remove ANY member?"*. `member-remove` additionally
 *     enforces `canRemoveByHierarchy(viewerLevel, targetLevel)` PER TARGET, and an
 *     OWNER is unremovable by anyone. A community MODERATOR reads `true` here, yet
 *     may remove only MEMBER/GUEST — rendering a Remove row on peer MODERATOR /
 *     ADMIN / OWNER rows on the strength of this signal alone produces a control
 *     that 403s on every tap. The target half needs the target's own level.
 *
 *     ⚠ **Self-removal is EXCLUDED.** `member-remove` short-circuits
 *     (`if (isSelfRemoval) continue`) BEFORE its permission check, so any member —
 *     including a plain MEMBER — can always remove themselves while this signal
 *     reads `false`. Never gate a "Leave space" control on it: that would break
 *     leaving for every non-moderator.
 *
 * NEVER infer one of these from another, and never from `canManageSpace` — they
 * front three different endpoints with three different gates. All three are
 * DISPLAY HINTS: the endpoints authorize independently (KTD8).
 *
 * The whole block is OPTIONAL on `SpaceDetail` so list→detail priming holds (a
 * `SpaceListItem` has no `viewer`) and a stale build / not-yet-deployed backend
 * still parses (skew safety); clients must fail closed to the member view when
 * it is absent.
 *
 * The three grant signals are `.optional().default(false)` INSIDE the block: an
 * older backend that sends only `{ canManageSpace, isMember }` still parses
 * (wire-compatible), but the fail-closed default is applied AT THE PARSE BOUNDARY
 * rather than left to every consumer to remember. This is a type guarantee, not a
 * convention — the parsed type is `boolean`, never `boolean | undefined`, so the
 * natural spelling of a disabled-row check (`viewer.canGrantAdmin === false`)
 * cannot silently render an ENABLED control against an old server, which is what
 * `undefined === false` would do. For a grant affordance the safe default is off.
 */
export const SpaceViewerSchema = z.object({
  canManageSpace: z.boolean(),
  isMember: z.boolean(),
  canGrantModerator: z.boolean().optional().default(false),
  canGrantAdmin: z.boolean().optional().default(false),
  canRemoveMembers: z.boolean().optional().default(false),
  /**
   * May this viewer POST in this space (plan 2026-07-16-001, U2 / R6)?
   *
   * Server-computed by the ONE posting-authority resolver (`resolve-post-authority.ts`)
   * — the resolver that will authorize the write once enforcement flips to it. TODAY
   * the write gate still ENFORCES via the legacy chain (U3 shadow-compares only, does
   * not yet decide — KTD5), so until that flip this DISPLAY verdict can diverge from
   * what the write actually allows: most notably it now shows the composer for
   * `memberPostingEnabled` members the legacy enforce chain has no reader for, whose
   * post then 403s. Ending that divergence is the TARGET this field exists for; it is
   * fully realized only when U3's enforcement cutover lands (re-measure the
   * disagreement rate first — KTD8). The original bug this replaces: the write gate
   * read `SchoolUsersOnGroups.canPost` with `=== true` while the display path read the
   * SAME column with `orTrue()`, so a null rendered the post button and then rejected
   * the post.
   *
   * Clients render the post affordance from THIS FIELD ALONE (R6/R8) — never from
   * cached session groups, membership rows, or `settings.memberPostingEnabled`.
   * Read it as `canPost === true`, never `canPost ?? true`: on a Zod parse failure
   * in production `typedGet` returns the raw unvalidated body, so a truthy
   * non-boolean would sail through `??` and OPEN the gate.
   *
   * `.optional().default(false)` for the same reason as the grant signals above —
   * fail-closed at the parse boundary, so an old server's absent field is `false`
   * rather than `undefined`. Posting is not a grant affordance, but the asymmetry
   * still favours closed: a wrongly-hidden button is recoverable, a wrongly-shown
   * one 403s the user after they have written their post.
   */
  canPost: z.boolean().optional().default(false),
  /**
   * Media-write viewer signals (plan 2026-07-22-002 U1 / R12 / KTD6). BOTH are
   * the media-authority resolver's OWN answer (`resolve-media-authority`,
   * called per action by spaces get-single) — never re-derived tier facts — so
   * display and enforcement agree by construction, exactly like `canPost`
   * fronts `resolve-post-authority`.
   *
   *   - `canCreateAlbum` — may this viewer CREATE an album in this space
   *     (R2 tier: leader / moderator grantee / admin; never plain members).
   *   - `canUploadMedia` — may this viewer ADD media to an existing album
   *     (R3: the R2 tier, plus active members when the space's
   *     `memberMediaEnabled` toggle is on).
   *
   * DISPLAY HINTS only — the media/collections endpoints authorize
   * independently server-side; a stale-true (e.g. mobile's persisted spaces
   * cache after a toggle flip) resolves as a graceful server 403, never an
   * escalation. Read fail-closed (`=== true`), never `?? true` — same
   * parse-boundary reasoning as `canPost` above.
   */
  canCreateAlbum: z.boolean().optional().default(false),
  canUploadMedia: z.boolean().optional().default(false)
})

export type SpaceViewer = z.infer<typeof SpaceViewerSchema>

export const SpaceDetailSchema = SpaceListItemSchema.omit({
  // The list row's top-level `canPost` is NEVER produced on the detail read — the
  // detail's posting signal is `viewer.canPost` (resolver-computed, fail-closed).
  // Inheriting the list field would leave `spaceDetail.canPost` permanently
  // `undefined` yet autocompletable, the "declared on the contract, produced by
  // nothing" trap this file guards elsewhere. Omit it so a consumer must read
  // `viewer.canPost`.
  canPost: true
}).extend({
  // Free-text space description (Section / UserGroup `description`). Null when unset.
  description: z.string().nullable(),
  // Feature toggles + notify default (see SpaceSettingsSchema).
  settings: SpaceSettingsSchema,
  // Optional roster summary for the detail header. Absent/null when not loaded.
  rosterSummary: z.object({ memberCount: z.number() }).nullable().optional(),
  // Capability flags the detail screen uses to gate tabs. Derived server-side
  // from the space kind / offering type — not yet leader-settable.
  assignmentsEnabled: z.boolean(),
  scheduleEnabled: z.boolean(),
  // Admin-only detail block, populated ONLY for admin viewers (ABSENT — not
  // null — for non-admins). Optional so it never narrows an inherited list-row
  // field to required, preserving list→detail priming. See AdminSpaceDetailSchema.
  adminDetail: AdminSpaceDetailSchema.optional(),
  // Server-computed viewer capability block (display hint, NOT a write gate).
  // Optional so list→detail priming holds and a stale build still parses; clients
  // fail closed to the member view when absent. See SpaceViewerSchema.
  viewer: SpaceViewerSchema.optional()
  // streamChatChannelId / streamChatStatus are INHERITED from SpaceListItemSchema
  // (nullable + optional). Do not redeclare — see file header.
})

export type SpaceDetail = z.infer<typeof SpaceDetailSchema>

export const SpaceDetailResponseSchema = z.object({
  data: SpaceDetailSchema,
  message: z.string()
})

export type SpaceDetailResponse = z.infer<typeof SpaceDetailResponseSchema>
